import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { MoAlert, MoSpacing } from '@motivo/guanyin/src/components';
import { useFrontContext } from '../hooks/useFrontContext';
import { entitiesByEmailsQuery } from '../graphql/queries';
import { LoadingSkeleton, ErrorDisplay, EmptyState } from './LoadingStates';
import EntitySelector, { getEntityKey } from './EntitySelector';
import EntityDetails from './EntityDetails';

function PluginContainer() {
  const { emails, loading: contextLoading, conversationType } = useFrontContext();
  const [selectedEntityKey, setSelectedEntityKey] = useState<string>('');

  const {
    data,
    loading: queryLoading,
    error,
  } = useQuery(entitiesByEmailsQuery, {
    variables: { emails },
    skip: emails.length === 0 || conversationType !== 'single',
  });

  const entities = data?.entitiesByEmails || [];

  useEffect(() => {
    if (entities.length > 0) {
      setSelectedEntityKey(getEntityKey(entities[0]));
    }
  }, [entities]);

  if (contextLoading || conversationType !== 'single') {
    return (
      <Box p={3}>
        <MoAlert severity="info">Select a conversation to view Motivo user information</MoAlert>
      </Box>
    );
  }

  if (queryLoading) {
    return (
      <Box p={3}>
        <LoadingSkeleton count={3} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <ErrorDisplay error={error} />
      </Box>
    );
  }

  if (entities.length === 0) {
    return (
      <Box p={3}>
        <EmptyState message="No Motivo users found in this conversation" />
      </Box>
    );
  }

  const selectedEntity = entities.find((entity) => getEntityKey(entity) === selectedEntityKey);

  return (
    <Box p={3}>
      <MoSpacing y={3}>
        {entities.length > 1 && (
          <EntitySelector
            entities={entities}
            value={selectedEntityKey}
            onChange={setSelectedEntityKey}
          />
        )}
        {selectedEntity && <EntityDetails entity={selectedEntity} />}
      </MoSpacing>
    </Box>
  );
}

export default PluginContainer;
